import React, { useState, useEffect } from 'react';
import { getData, getUserProfile } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { exportToCSV, exportToPDF } from '../exportUtils';

const BRAND = '#0f6e56';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Reports({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [inv, exp, prof] = await Promise.all([getData(user.uid,'invoices'), getData(user.uid,'expenses'), getUserProfile(user.uid)]);
      setInvoices(inv); setExpenses(exp); setProfile(prof); setLoading(false);
    };
    load();
  }, [user.uid]);

  const fmt = n => '$' + Number(n||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });

  const income = invoices.filter(i => i.type==='sales' && i.status==='paid').reduce((s,i) => s+i.amount, 0);
  const unpaid = invoices.filter(i => i.type==='sales' && i.status==='unpaid').reduce((s,i) => s+i.amount, 0);
  const cogs = invoices.filter(i => i.type==='purchase').reduce((s,i) => s+i.amount, 0);
  const totalExp = expenses.reduce((s,e) => s+e.amount, 0);
  const grossProfit = income - cogs;
  const netProfit = grossProfit - totalExp;
  const grossMargin = income > 0 ? ((grossProfit/income)*100).toFixed(1) : '0.0';
  const netMargin = income > 0 ? ((netProfit/income)*100).toFixed(1) : '0.0';

  const monthlyData = MONTHS.map((m, i) => {
    const mIncome = invoices.filter(inv => inv.type==='sales' && inv.status==='paid' && new Date(inv.date).getMonth()===i).reduce((s,inv)=>s+inv.amount,0);
    const mExp = expenses.filter(e => new Date(e.date).getMonth()===i).reduce((s,e)=>s+e.amount,0);
    return { name:m, Income:mIncome, Expenses:mExp };
  });

  const expByCategory = {};
  expenses.forEach(e => { expByCategory[e.category] = (expByCategory[e.category]||0)+e.amount; });

  if (loading) return <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>Loading reports...</div>;

  const handlePDF = () => {
    let body = `<div class="section-title">Financial Summary</div><table><tbody>`;
    body += `<tr><td>Total Revenue</td><td class="num">${fmt(income)}</td></tr>`;
    body += `<tr><td>Cost of Goods Sold</td><td class="num">${fmt(cogs)}</td></tr>`;
    body += `<tr><td>Gross Profit</td><td class="num">${fmt(grossProfit)}</td></tr>`;
    body += `<tr><td>Operating Expenses</td><td class="num">${fmt(totalExp)}</td></tr>`;
    body += `<tr class="total"><td>Net Profit</td><td class="num green">${fmt(netProfit)}</td></tr>`;
    body += `<tr><td>Gross Margin</td><td class="num">${grossMargin}%</td></tr>`;
    body += `<tr><td>Net Margin</td><td class="num">${netMargin}%</td></tr></tbody></table>`;
    body += `<div class="section-title">Monthly Breakdown</div><table><thead><tr><th>Month</th><th class="num">Income</th><th class="num">Expenses</th></tr></thead><tbody>`;
    monthlyData.forEach(m => { body += `<tr><td>${m.name}</td><td class="num">${fmt(m.Income)}</td><td class="num">${fmt(m.Expenses)}</td></tr>`; });
    body += `</tbody></table>`;
    exportToPDF('Financial Report', profile?.businessName, body);
  };
  const handleCSV = () => {
    const rows = [['Total Revenue', income], ['COGS', cogs], ['Gross Profit', grossProfit], ['Operating Expenses', totalExp], ['Net Profit', netProfit], ['Gross Margin', grossMargin+'%'], ['Net Margin', netMargin+'%'], ['',''], ['Month','Income','Expenses']];
    monthlyData.forEach(m => rows.push([m.name, m.Income, m.Expenses]));
    exportToCSV('FinancialReport', ['Metric','Value'], rows);
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:700 }}>Reports & Analytics</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={handlePDF} style={{ padding:'8px 16px', background:'#fcebeb', color:'#a32d2d', border:'1px solid #a32d2d30', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer' }}>📄 PDF</button>
          <button onClick={handleCSV} style={{ padding:'8px 16px', background:'#e6f1fb', color:'#185fa5', border:'1px solid #185fa530', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer' }}>📊 Excel</button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16, marginBottom:24 }}>
        {[
          { label:'Total Revenue', value:fmt(income), color:BRAND, bg:'#e1f5ee' },
          { label:'Gross Profit', value:fmt(grossProfit), color:'#185fa5', bg:'#e6f1fb' },
          { label:'Net Profit', value:fmt(netProfit), color: netProfit>=0?'#185fa5':'#a32d2d', bg: netProfit>=0?'#e6f1fb':'#fcebeb' },
          { label:'Outstanding', value:fmt(unpaid), color:'#854f0b', bg:'#faeeda' },
          { label:'Gross Margin', value:grossMargin+'%', color:BRAND, bg:'#e1f5ee' },
          { label:'Net Margin', value:netMargin+'%', color:'#185fa5', bg:'#e6f1fb' },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:16 }}>
            <div style={{ fontSize:12, color:'#555', marginBottom:6, textTransform:'uppercase', letterSpacing:0.3 }}>{s.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ background:'#fff', borderRadius:14, padding:24, border:'1px solid #e8e8e8', marginBottom:20 }}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:20 }}>Monthly Overview</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthlyData}>
            <XAxis dataKey="name" tick={{ fontSize:12 }} />
            <YAxis tick={{ fontSize:12 }} />
            <Tooltip formatter={v => fmt(v)} />
            <Legend />
            <Bar dataKey="Income" fill={BRAND} radius={[4,4,0,0]} />
            <Bar dataKey="Expenses" fill="#e24b4a" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:24, border:'1px solid #e8e8e8' }}>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Profit & Loss Summary</div>
          {[
            ['Revenue', fmt(income), BRAND],
            ['Cost of Goods (COGS)', fmt(cogs), '#a32d2d'],
            ['Gross Profit', fmt(grossProfit), '#185fa5'],
            ['Operating Expenses', fmt(totalExp), '#854f0b'],
            ['Net Profit', fmt(netProfit), netProfit>=0 ? BRAND : '#a32d2d'],
          ].map(([l,v,c]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #f0f0f0' }}>
              <span style={{ fontSize:14, color:'#555' }}>{l}</span>
              <span style={{ fontSize:14, fontWeight:600, color:c }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ background:'#fff', borderRadius:14, padding:24, border:'1px solid #e8e8e8' }}>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Expenses by Category</div>
          {Object.entries(expByCategory).sort((a,b)=>b[1]-a[1]).map(([cat,amt]) => (
            <div key={cat} style={{ marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:13, color:'#555' }}>{cat}</span>
                <span style={{ fontSize:13, fontWeight:600, color:'#a32d2d' }}>{fmt(amt)}</span>
              </div>
              <div style={{ height:6, background:'#f0f0f0', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', background:'#e24b4a', borderRadius:3, width:`${Math.round((amt/totalExp)*100)}%` }} />
              </div>
            </div>
          ))}
          {Object.keys(expByCategory).length === 0 && <div style={{ color:'#aaa', textAlign:'center', padding:20 }}>No expenses yet</div>}
        </div>
      </div>
    </div>
  );
}
